import { NetworkSymbols } from "based-auth/networks";
import { Schema, model, models, Model, Types, Mixed } from "mongoose";

export interface ICommentContent {
  type: "text" | "image";
  data: string;
}

export interface IUserRef {
  address: string;
  network: NetworkSymbols;
}

export interface IComment extends Document {
  _id: string;
  chainId: number;
  address: string;
  wroteBy: IUserRef;
  content: ICommentContent[];
  timestamp: number;
  updatedAt: number | null;
  likedBy: IUserRef[];
  dislikedBy: IUserRef[];
  parentId: string | null;
  isEdited: boolean;
  score: number;
  getChildren(): Promise<IComment[]>;
}

const userRefSchema = {
  address: { type: String, required: true },
  network: {
    type: String,
    enum: Object.values(NetworkSymbols),
    required: true,
  },
};

const commentSchema: Schema<IComment> = new Schema<IComment>(
  {
    chainId: { type: Number, required: true },
    address: { type: String, required: true },
    wroteBy: userRefSchema,
    content: [
      {
        type: {
          type: String,
          enum: ["text", "image"],
          required: true,
        },
        data: {
          type: String,
          default: "",
        },
      },
    ],
    timestamp: {
      type: Number,
      default: Date.now,
    },
    updatedAt: {
      type: Number,
      default: null,
    },
    likedBy: {
      type: [userRefSchema],
      default: [],
    },
    dislikedBy: {
      type: [userRefSchema],
      default: [],
    },
    parentId: {
      type: String,
      default: null,
    },
    isEdited: {
      type: Boolean,
      default: false,
    },
  },
  {
    timestamps: true,
    toObject: {
      getters: true,
      versionKey: false,
      transform: (doc, ret: any) => {
        ret.id = ret._id.toString();
        delete ret._id;
        for (const i of ret.content) {
          delete i._id;
        }
        for (const i of ret.likedBy) {
          delete i._id;
        }
        for (const i of ret.dislikedBy) {
          delete i._id;
        }
      },
    },
  }
);

// Virtual property to calculate score
commentSchema.virtual("score").get(function (this: IComment) {
  return this.likedBy.length - this.dislikedBy.length;
});

commentSchema.index(
  { chainId: 1, address: 1, wroteBy: 1, timestamp: 1 },
  { unique: true }
);

commentSchema.methods.getChildren = async function (this: IComment) {
  return await CommentModel.find({ parentId: this._id });
};

// Pre-save middleware to update the updatedAt field and clean content
// commentSchema.pre("save", function (this: any, next) {
//   if (this.isModified("content")) {
//     this.updatedAt = Date.now();
//     this.isEdited = true;
//   }

//   // Clean only completely missing content items before saving
//   if (this.content && Array.isArray(this.content)) {
//     this.content = this.content.filter(
//       (item: any) =>
//         item && item.type && item.data !== undefined && item.data !== null
//     );
//   }

//   next();
// });

export const CommentModel: Model<IComment> =
  models?.Comment || model<IComment>("Comment", commentSchema);
